"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import styles from "./landing.module.css";
import { NavbarLogo } from "@/components/NavbarLogo";
import { useLandingLogin } from "./LandingLoginProvider";
import { getAuthToken, isNormalUserSession, removeAuthToken, verifyToken } from "@/lib/auth";
import { resolvePostLoginRedirectOrDashboardAsync } from "@/lib/postLoginRedirect";
import { isEnterpriseLoginEnabled } from "@/lib/enterpriseLoginFeature";
import { landingCtaClick } from '@/lib/ga'
import { Building2 } from "lucide-react";

/**
 * Landing header with Login opening the meritlens login popup (used on landing only).
 * With a stored auth token, shows Go to app (validated in the background via current-user).
 * Other app entry points (navbar, session-expired, etc.) still go to /signin.
 */
export function LandingNavWithLogin({
  variant = 'fixed',
  activeLink,
}: {
  variant?: 'fixed' | 'sticky'
  /** Pass 'about' to highlight the About Us nav link with the brand gradient. */
  activeLink?: 'about'
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { openLoginModal } = useLandingLogin();
  // A B2C (normal user) session flips "Login" to "Go to app". An enterprise/recruiter
  // session (or no session) leaves this alone — the B2C landing page always shows
  // Login and Enterprise as separate options regardless of enterprise login state.
  const [showNormalGoToApp, setShowNormalGoToApp] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setShowNormalGoToApp(isNormalUserSession());
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setShowNormalGoToApp(false);
      return;
    }

    let cancelled = false;
    verifyToken().then((result) => {
      if (cancelled) return;
      if (!result.valid) {
        removeAuthToken();
        setShowNormalGoToApp(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleGoToApp = async () => {
    const path = await resolvePostLoginRedirectOrDashboardAsync();
    router.push(path);
  };

  const closeMenu = () => setMenuOpen(false);

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    closeMenu();
    if (pathname !== "/") return;
    e.preventDefault();
    window.history.replaceState(null, "", "/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLoginClick = () => {
    closeMenu();
    landingCtaClick({ cta_surface: 'navbar', journey: 'login_only' });
    openLoginModal({ loginEntry: 'navbar' });
  };

  const authButton = showNormalGoToApp ? (
    <button
      type="button"
      className={styles.cta}
      onClick={() => { closeMenu(); void handleGoToApp(); }}
      aria-label="Go to app"
    >
      Go to app
    </button>
  ) : isEnterpriseLoginEnabled() ? (
    <div className={styles.authActionGroup}>
      <button
        type="button"
        className={styles.cta}
        onClick={handleLoginClick}
        aria-label="Log in to meritlens"
      >
        Login
      </button>
      <button
        type="button"
        className={styles.ctaOutline}
        onClick={() => { closeMenu(); router.push("/enterprise"); }}
        aria-label="Sign in for enterprises and teams"
      >
        <Building2 className={styles.ctaIcon} size={16} strokeWidth={2} aria-hidden />
        Enterprise
      </button>
    </div>
  ) : (
    <button
      type="button"
      className={styles.cta}
      onClick={handleLoginClick}
      aria-label="Log in to meritlens"
    >
      Login
    </button>
  );

  return (
    <>
      <header className={variant === 'sticky' ? styles.navBarSticky : styles.navBar}>
        <div className={styles.navInner}>
          <NavbarLogo href="/" priority aria-label="meritlens home" />

          {/* Desktop nav */}
          <nav className={styles.navRight} aria-label="Landing navigation">
            <div className={styles.navLinksCluster}>
              <Link href="/" className={styles.navLink} onClick={handleHomeClick}>Home</Link>
              <Link href="/#interview-formats" className={styles.navLink}>Interview Formats</Link>
              <Link href="/#practice-questions" className={styles.navLink}>Practice Questions</Link>
              <Link
                href="/about"
                className={styles.navLink}
                style={
                  activeLink === 'about'
                    ? {
                      background: 'linear-gradient(176.39deg, #7f75ff 0.53%, #4d42ce 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontWeight: 600,
                    }
                    : undefined
                }
              >
                About Us
              </Link>
            </div>
            {/* Auth button shown on desktop only */}
            <div className={styles.desktopAuthGroup}>
              {authButton}
            </div>
          </nav>

          {/* Hamburger button — mobile only */}
          <button
            type="button"
            className={styles.hamburger}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className={`${styles.hamburgerLine} ${menuOpen ? styles.hamburgerLineTop : ''}`} />
            <span className={`${styles.hamburgerLine} ${menuOpen ? styles.hamburgerLineMid : ''}`} />
            <span className={`${styles.hamburgerLine} ${menuOpen ? styles.hamburgerLineBot : ''}`} />
          </button>
        </div>

        {/* Mobile drawer — slides down from navbar */}
        <div className={`${styles.mobileDrawer} ${menuOpen ? styles.mobileDrawerOpen : ''}`} aria-hidden={!menuOpen}>
          <div className={styles.mobileDrawerInner}>
            <nav className={styles.mobileDrawerNav}>
              <Link href="/" className={styles.mobileNavLink} onClick={handleHomeClick}>Home</Link>
              <Link href="/#interview-formats" className={styles.mobileNavLink} onClick={closeMenu}>Interview Formats</Link>
              <Link href="/#practice-questions" className={styles.mobileNavLink} onClick={closeMenu}>Practice Questions</Link>
              <Link
                href="/about"
                className={styles.mobileNavLink}
                onClick={closeMenu}
                style={
                  activeLink === 'about'
                    ? {
                      background: 'linear-gradient(176.39deg, #7f75ff 0.53%, #4d42ce 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontWeight: 600,
                    }
                    : undefined
                }
              >
                About Us
              </Link>
            </nav>
            <div className={styles.mobileDrawerAuth}>
              {authButton}
            </div>
          </div>
        </div>
      </header>

      {/* Backdrop overlay */}
      {menuOpen && (
        <div
          className={styles.mobileDrawerBackdrop}
          aria-hidden="true"
          onClick={closeMenu}
        />
      )}
    </>
  );
}
